// src/lib/composeDraft.js — what a compose window IS, as data, so it can be
// saved, reloaded, sent, undone and recovered without losing anything.
//
// Pure: no DOM, no Supabase, no React. ComposeEmailModal, the compose provider
// and the send queue all go through these helpers.
//
// The rules (each one is a past failure):
//   • Every compose is backed up — new messages, replies, forwards and Janet
//     prefills alike — with its whole identity: recipients, subject, the body,
//     the quoted original, the thread it replies in, the Gmail draft it saves
//     to, and its attachments.
//   • Attachments are references when Gmail already holds the file (a saved
//     draft's part, or the original being forwarded) and bytes only while a
//     picked file has not yet reached Gmail. Bytes too large for this device's
//     storage are dropped from the LOCAL copy only, and the window says so.
//   • Nothing recoverable is cleared before a send is confirmed. A send that
//     is in flight lives under its own pending key until Gmail confirms it.
//   • A retry reuses the send's key, so the server can tell a lost response
//     from a message that never went out (api/_lib/send-idempotency.js).

export const COMPOSE_DRAFT_PREFIX = 'chatrone:compose-draft'
export const PENDING_SEND_PREFIX = 'chatrone:compose-pending'
/** How long Undo Send can stop a message: it is not handed to Gmail before this. */
export const UNDO_SEND_MS = 10_000
const LOCAL_TTL_MS = 14 * 24 * 60 * 60 * 1000
/** Bytes of picked files kept in this device's storage (localStorage is ~5 MB). */
export const LOCAL_ATTACHMENT_BUDGET = 1_500_000

export const QUOTE_CLASS = 'chatrone-quoted-reply'
const QUOTE_OPEN = `<div class="${QUOTE_CLASS}">`

// ── Body + quoted original ────────────────────────────────────────────────

/** The HTML that goes to Gmail (draft or send): the body, then the quoted original. */
export function joinBodyAndQuote(bodyHtml, quotedHtml, quotedVisible = true) {
  const body = bodyHtml || ''
  if (!quotedHtml || !quotedVisible) return body
  return `${body}<br>${QUOTE_OPEN}${quotedHtml}</div>`
}

/**
 * Split a saved draft back into the editable body and the quoted original.
 * Recognises our own wrapper and Gmail's (a draft edited in Gmail keeps
 * Gmail's `gmail_quote`). With neither, the whole thing is the body.
 */
export function splitBodyAndQuote(html) {
  const s = String(html || '')
  const ours = s.indexOf(QUOTE_OPEN)
  if (ours >= 0) {
    let body = s.slice(0, ours)
    if (body.endsWith('<br>')) body = body.slice(0, -4)
    let quoted = s.slice(ours + QUOTE_OPEN.length)
    if (quoted.endsWith('</div>')) quoted = quoted.slice(0, -6)
    return { bodyHtml: body, quotedHtml: quoted }
  }
  const m = s.match(/<div[^>]*class="[^"]*gmail_quote[^"]*"/i)
  if (m) return { bodyHtml: s.slice(0, m.index), quotedHtml: s.slice(m.index) }
  return { bodyHtml: s, quotedHtml: '' }
}

// ── Attachments ───────────────────────────────────────────────────────────

let seq = 0
const attId = () => `a${Date.now().toString(36)}${(seq++).toString(36)}`

export function localAttachment({ filename, size, mimeType, base64 }) {
  return { id: attId(), source: 'local', filename, size: size || 0, mimeType: mimeType || 'application/octet-stream', data: base64 }
}

/**
 * Files of a message being forwarded, as Gmail references. Inline images
 * (signature logos, embedded pictures) are left out — they belong to the body.
 */
export function forwardAttachments(gmailMessageId, attachments) {
  if (!gmailMessageId) return []
  return (attachments || [])
    .filter(a => a && a.filename && !a.content_id && !a.inline)
    .map(a => ({
      id: attId(),
      source: 'gmail',
      origin: 'forward',
      messageId: gmailMessageId,
      attachmentId: a.attachment_id || a.attachmentId || null,
      filename: a.filename,
      size: a.size || 0,
      mimeType: a.mime_type || a.mimeType || 'application/octet-stream',
    }))
}

/** Wire shape for /api/save-draft and /api/send-gmail. */
export function attachmentsForWire(attachments) {
  return (attachments || []).flatMap(a => {
    if (a.source === 'gmail') {
      return [{ source: 'gmail', messageId: a.messageId, attachmentId: a.attachmentId || null, filename: a.filename, mimeType: a.mimeType, size: a.size }]
    }
    return a.data ? [{ filename: a.filename, mimeType: a.mimeType, data: a.data }] : []
  })
}

/** A picked file whose bytes were lost (device storage) and must be added again. */
export const attachmentNeedsReattach = a => a.source !== 'gmail' && !a.data

/**
 * After a draft save, Gmail holds the files: swap each local file for its
 * reference (matched by name + size, in order). Files Gmail did not report
 * stay as they were — a missing ref never drops a file.
 */
export function adoptDraftAttachmentRefs(current, refs) {
  if (!Array.isArray(refs)) return current
  const pool = refs.slice()
  return (current || []).map(a => {
    const i = pool.findIndex(r => r.filename === a.filename && (!a.size || !r.size || r.size === a.size))
    if (i < 0) return a
    const [r] = pool.splice(i, 1)
    return { id: a.id, source: 'gmail', origin: a.origin || 'draft', messageId: r.messageId, attachmentId: r.attachmentId || null, filename: r.filename, size: r.size || a.size, mimeType: r.mimeType || a.mimeType }
  })
}

// ── Snapshot (the whole window as data) ──────────────────────────────────

export function snapshotCompose(s) {
  return {
    to: s.to || '', cc: s.cc || '', bcc: s.bcc || '',
    subject: s.subject || '',
    bodyHtml: s.bodyHtml || '',
    quotedHtml: s.quotedHtml || '',
    quotedVisible: s.quotedVisible !== false,
    inReplyTo: s.inReplyTo || null,
    threadId: s.threadId || null,
    emailIntelligenceId: s.emailIntelligenceId || null,
    gmailDraftId: s.gmailDraftId || null,
    gmailDraftMessageId: s.gmailDraftMessageId || null,
    attachments: (s.attachments || []).map(a => ({ ...a })),
    // Janet / send-as context carried from the prefill, so a restored window
    // still sends from the right address and still learns from the edit.
    context: s.context || null,
  }
}

export function hasComposeContent(s) {
  if (!s) return false
  const text = String(s.bodyHtml || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
  return !!(String(s.to || '').trim() || String(s.subject || '').trim() || text || (s.attachments || []).length || s.inReplyTo)
}

/**
 * The copy kept in this device's storage. Picked-file bytes are kept while
 * they fit the budget; beyond it they are dropped (data: null) so the window
 * can say exactly which files must be added again — never silently.
 */
export function snapshotForDevice(snap) {
  let budget = LOCAL_ATTACHMENT_BUDGET
  const attachments = (snap.attachments || []).map(a => {
    if (a.source === 'gmail' || !a.data) return a
    const cost = a.data.length
    if (cost <= budget) { budget -= cost; return a }
    return { ...a, data: null }
  })
  return { ...snap, attachments, savedAt: Date.now() }
}

export function isFreshDeviceCopy(saved, now = Date.now()) {
  return !!saved && now - (saved.savedAt || 0) <= LOCAL_TTL_MS
}

// ── Keys ──────────────────────────────────────────────────────────────────

// One device copy per docked compose window (keyed by windowId) so two or
// three in-flight composes never clobber each other. The bare pre-multi-
// window key maps to the sentinel id 'legacy'.
export function draftKeyFor(windowId) {
  return windowId && windowId !== 'legacy' ? `${COMPOSE_DRAFT_PREFIX}:${windowId}` : COMPOSE_DRAFT_PREFIX
}
export const pendingKeyFor = sendKey => `${PENDING_SEND_PREFIX}:${sendKey}`

// cs-<minted ms, base36>-<random>: the time lets a retry check Sent back to
// the first attempt (api/_lib/send-idempotency.js sendKeyMintedAt).
export function newSendKey(now = Date.now()) {
  const rand = (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2))
  return `cs-${now.toString(36)}-${rand}`.replace(/[^A-Za-z0-9:_-]/g, '')
}

// ── Send outcome ──────────────────────────────────────────────────────────

/**
 * What a send attempt's result means.
 *   'sent'     Gmail took it (or an earlier attempt already had).
 *   'retry'    we do not know, or the server could not finish: retry later
 *              with the SAME key — the server reports an earlier copy rather
 *              than sending twice.
 *   'rejected' the server refused the message itself (bad recipient, too
 *              large, Gmail not connected): retrying cannot help; give the
 *              person their draft back.
 */
export function classifySendOutcome({ ok, status, networkError } = {}) {
  if (networkError) return 'retry'
  if (ok) return 'sent'
  if (status === 408 || status === 425 || status === 429 || (status >= 500 && status < 600)) return 'retry'
  if (status === 401) return 'retry' // signed out mid-send: the queue resumes after sign-in
  return 'rejected'
}

// ── Gmail draft conflict ──────────────────────────────────────────────────

/** How a /api/save-draft response changes the window's save state. */
export function interpretDraftSave(status, body) {
  if (status === 200 && body?.draftId) return { kind: 'saved', draftId: body.draftId, messageId: body.messageId || null, attachments: body.attachments ?? null }
  if (status === 409 && body?.conflict) return { kind: 'conflict', current: body.current || null }
  if (status === 410 && body?.missing) return { kind: 'missing' }
  return { kind: 'error', message: body?.error || `Save failed (${status})` }
}

/** What to tell the person when an unconfirmed send reopens after a reload. */
export function pendingSendNotice(state, reason = null) {
  if (state === 'rejected') return { tone: 'error', text: `Not sent — Gmail refused this message${reason ? `: ${String(reason).replace(/^HTTP \d{3}\s*—?\s*/, '')}` : '.'} Fix it and send again.` }
  if (state === 'undo') return { tone: 'warn', text: 'Not sent — the page closed before the undo window ended. Nothing went out.' }
  return { tone: 'warn', text: 'This message may already have been sent — the page closed while it was sending. Check Sent; sending it again will not create a second copy.' }
}
